import { useMemo } from 'react'
import { ILocation, Maybe } from 'src/types/graphql'
import { numberRounedUpWithoutDecimals } from 'src/utils'

interface ConfigSummary {
  hasMultipleConfigurations: boolean
  numConfigurations: number
  sizeRangeText: string | null
  /**
   * Sum of `availability` across active configs (parking card's
   * "N disponibles" pill). Unlike the other summary fields this is
   * meaningful for single-config listings too; `null` only on thin/legacy
   * shapes without `configSummary` — callers fall back to
   * `defaultConfiguration.availability`.
   */
  totalAvailability: number | null
}

/**
 * Multi-configuration summary for the search / recommendation card.
 *
 * Reads the server-precomputed `location.configSummary` (active count, size
 * range and availability) instead of the raw
 * `configurations[]` array — that array was dropped from the shared CARD
 * fragment to keep search-page `__NEXT_DATA__` small (~30-40 kB / page). See
 * `docs/performance/large-page-data.md`.
 *
 * The range / active rules MUST mirror `computeConfigSummary` in
 * `spotme-server/src/api/location/prices.ts` — change both in the same PR.
 *
 * When `configSummary` is absent (thin shapes / legacy cached clients), this
 * degrades to "single config" (all summary fields null), and the card falls
 * back to `defaultConfiguration` — exactly the pre-`configSummary` behavior for
 * a single-configuration listing.
 */
export default function useConfigSummary(
  location: Maybe<ILocation> | undefined
): ConfigSummary {
  const summary = location?.configSummary

  return useMemo(() => {
    const numConfigurations = summary?.activeCount ?? 0
    const hasMultipleConfigurations = numConfigurations > 1
    const totalAvailability = summary?.totalAvailability ?? null

    if (!summary || !hasMultipleConfigurations) {
      return {
        hasMultipleConfigurations,
        numConfigurations,
        sizeRangeText: null,
        totalAvailability
      }
    }

    const minArea = numberRounedUpWithoutDecimals(summary.minSizeM2 ?? 0)
    const maxArea = numberRounedUpWithoutDecimals(summary.maxSizeM2 ?? 0)
    const sizeRangeText =
      minArea === maxArea ? `${minArea} m²` : `${minArea} – ${maxArea} m²`

    return {
      hasMultipleConfigurations,
      numConfigurations,
      sizeRangeText,
      totalAvailability
    }
  }, [summary])
}
